import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import MobileTabs, { Tab } from './MobileTabs'
import { makeStyles } from 'tss-react/mui'
import { Box } from '@mui/material'

interface StyleProps {
  hasSmallTopMargin?: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  content: {
    width: '100%',
    marginTop: props.hasSmallTopMargin ? theme.spacing(4) : theme.spacing(6),
    [theme.breakpoints.down('md')]: {
      marginTop: props.hasSmallTopMargin ? theme.spacing(4) : theme.spacing(6),
      marginBottom: theme.spacing(6),
    },
  },
}))

export interface MobileContentProps {
  tabs: Tab[]
  content: React.ReactNode[]
  className?: string
  tabsClassName?: string
  elevation?: number
  additionalContent?: React.ReactNode
  fixAdditionalContent?: boolean // if false it will scroll away
  initialTab?: number
  topMargin?: number
  hasSmallTopMargin?: boolean
  enableTopMargin?: boolean
  setCurrentTab?: (value: number) => void
  setCurrPage?: (value: number) => void
  resetPageAfterTabChange?: boolean
  showOnlyTabIconsOnSmallSize?: boolean
}

const MobileContent: React.FC<MobileContentProps> = ({
  tabs,
  content,
  className,
  tabsClassName,
  elevation,
  additionalContent,
  fixAdditionalContent,
  initialTab,
  setCurrentTab,
  setCurrPage,
  topMargin,
  hasSmallTopMargin,
  enableTopMargin,
  resetPageAfterTabChange = true,
  showOnlyTabIconsOnSmallSize,
}) => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(initialTab ?? 0)
  const { classes, cx } = useStyles({ hasSmallTopMargin })

  /**
   * Because of the features like remembering last tab, the initialTab is not a constant anymore
   * and can change. In case it changes we want to update the activeTab to the new initialTab
   */
  useEffect(() => {
    if (initialTab !== undefined && initialTab !== activeTab) {
      setActiveTab(initialTab)
    }
  }, [activeTab, initialTab])

  useEffect(() => {
    for (let index = 0; index < tabs.length; index += 1) {
      if (location.pathname.startsWith(tabs[index].url)) {
        if (activeTab !== index) {
          setActiveTab(index)
        }
        return
      }
    }
  }, [location, tabs]) // eslint-disable-line

  const tabsComponent = useMemo(
    () => (
      <MobileTabs
        tabs={tabs}
        activeTab={activeTab}
        elevation={elevation}
        additionalContent={fixAdditionalContent ? additionalContent : undefined}
        topMargin={topMargin}
        setActiveTab={setActiveTab}
        setCurrentTab={setCurrentTab}
        setCurrPage={setCurrPage}
        enableTopMargin={enableTopMargin}
        className={tabsClassName}
        resetPageAfterTabChange={resetPageAfterTabChange}
        showOnlyIconsOnSmallSize={showOnlyTabIconsOnSmallSize}
      />
    ),
    [
      tabs,
      activeTab,
      tabsClassName,
      setCurrentTab,
      setCurrPage,
      enableTopMargin,
      elevation,
      fixAdditionalContent,
      additionalContent,
      topMargin,
      resetPageAfterTabChange,
      showOnlyTabIconsOnSmallSize,
    ]
  )

  return (
    <>
      {!fixAdditionalContent && additionalContent}
      {tabsComponent}
      <Box className={cx(className, classes.content)}>{content[activeTab]}</Box>
    </>
  )
}

export default MobileContent
